import {
  Bell,
  ClipboardCheck,
  FileClock,
  FileText,
  Gauge,
  Leaf,
  ListChecks,
  Menu,
  MessageSquareWarning,
  NotebookTabs,
  ScrollText,
  Settings2,
  KeyRound,
  LogOut,
  Trees,
  Users,
  X,
} from "lucide-react";
import { useState, type ReactNode } from "react";
import type { Me } from "../lib/types";
import { signOut } from "../lib/api";
import { PasswordChangeDialog } from "./PasswordChangeDialog";

interface NavigationItem {
  readonly to: string;
  readonly label: string;
  readonly icon: typeof Leaf;
  readonly roles: readonly string[];
}

const navigation: readonly NavigationItem[] = [
  { to: "/", label: "Overview", icon: Gauge, roles: ["all"] },
  {
    to: "/requests",
    label: "Requests",
    icon: FileText,
    roles: ["manager", "administrator"],
  },
  {
    to: "/assignments",
    label: "Assignments",
    icon: NotebookTabs,
    roles: ["manager", "administrator", "auditor"],
  },
  {
    to: "/reviews",
    label: "Review queue",
    icon: ClipboardCheck,
    roles: ["verifier", "administrator"],
  },
  {
    to: "/inspections",
    label: "Inspections",
    icon: ListChecks,
    roles: ["verifier", "administrator"],
  },
  {
    to: "/moderation",
    label: "Community reports",
    icon: MessageSquareWarning,
    roles: ["verifier", "administrator"],
  },
  {
    to: "/plantings",
    label: "Plantings",
    icon: Trees,
    roles: ["manager", "verifier", "administrator", "auditor"],
  },
  {
    to: "/reports",
    label: "Reports",
    icon: FileClock,
    roles: ["manager", "verifier", "administrator", "auditor"],
  },
  {
    to: "/policies",
    label: "Policies",
    icon: Settings2,
    roles: ["administrator", "auditor"],
  },
  {
    to: "/people",
    label: "People & roles",
    icon: Users,
    roles: ["administrator"],
  },
  {
    to: "/audit",
    label: "Audit",
    icon: ScrollText,
    roles: ["administrator", "auditor"],
  },
  {
    to: "/notifications",
    label: "Notifications",
    icon: Bell,
    roles: ["all"],
  },
];

function canSee(item: NavigationItem, roles: readonly string[]): boolean {
  return (
    item.roles.includes("all") ||
    item.roles.some((role) => roles.includes(role))
  );
}

export function Layout({
  me,
  path,
  children,
  onSignedOut,
}: {
  readonly me: Me;
  readonly path: string;
  readonly children: ReactNode;
  readonly onSignedOut: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  return (
    <div className="shell">
      <a className="skip-link" href="#main">
        Skip to main content
      </a>
      <header className="mobile-header">
        <button
          className="icon-button"
          type="button"
          aria-label={open ? "Close navigation" : "Open navigation"}
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
        >
          {open ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
        </button>
        <Brand />
      </header>
      <aside className={`sidebar ${open ? "open" : ""}`}>
        <Brand />
        <p className="program-label">Quezon Province staff console</p>
        <nav aria-label="Staff navigation">
          {navigation
            .filter((item) => canSee(item, me.roles))
            .map((item) => {
              const Icon = item.icon;
              return (
                <a
                  key={item.to}
                  href={item.to}
                  aria-current={path === item.to ? "page" : undefined}
                  onClick={() => setOpen(false)}
                >
                  <Icon aria-hidden="true" />
                  {item.label}
                </a>
              );
            })}
        </nav>
        <div className="staff-card">
          <span className="staff-avatar" aria-hidden="true">
            {me.display_name.slice(0, 1)}
          </span>
          <span>
            <strong>{me.display_name}</strong>
            <small>{me.roles.join(" · ")}</small>
          </span>
          <button
            className="staff-action"
            type="button"
            aria-label="Change password"
            onClick={() => setPasswordOpen(true)}
          >
            <KeyRound aria-hidden="true" />
          </button>
          <button
            className="staff-action"
            type="button"
            aria-label="Sign out"
            onClick={() => void signOut().then(onSignedOut)}
          >
            <LogOut aria-hidden="true" />
          </button>
        </div>
      </aside>
      {open ? (
        <button
          className="nav-scrim"
          type="button"
          aria-label="Close navigation"
          onClick={() => setOpen(false)}
        />
      ) : null}
      <main id="main" className="main">
        {children}
      </main>
      {passwordOpen ? (
        <PasswordChangeDialog
          onCancel={() => setPasswordOpen(false)}
          onChanged={async () => {
            await signOut();
            onSignedOut();
          }}
        />
      ) : null}
    </div>
  );
}

function Brand() {
  return (
    <div className="brand">
      <span className="brand-mark" aria-hidden="true">
        <Leaf />
      </span>
      <span>BalikTANiman</span>
    </div>
  );
}
